import { useMemo, useState } from "react";
import { productMatches, searchWords } from "@/lib/search";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { eur, num, dateParams, previousRange } from "@/lib/format";
import { SeasonSelect } from "@/components/materials/SeasonSelect";
import { PageHeader } from "@/components/PageHeader";
import DateRangeFilter from "@/components/DateRangeFilter";
import { Delta } from "@/components/Delta";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Minus, Loader2, Scissors, Shirt, CalendarClock, CheckCircle2, Package2, PackageMinus, AlertTriangle, Search } from "lucide-react";
import { toast } from "sonner";
import { NewBatchDialog } from "@/components/NewBatchDialog";
import { BatchSizes, LabInfo, PhaseTimeline } from "@/components/BatchDetails";
import { LabsSummary } from "@/components/LabsSummary";
import { DefectsReport } from "@/components/DefectsReport";
import { CuttingTab } from "@/components/cutting/CuttingTab";
import { DeleteBatchButton } from "@/components/DeleteBatchButton";
import { EditQuantitiesButton, QuantityChanges } from "@/components/EditBatchQuantities";
import { PhaseMoveDialog, StockMoves } from "@/components/production/PhaseMoveDialog";

// Lotti della stessa scheda taglio in un solo riquadro: viaggiano insieme, materiali divisi per colore
const groupLots = (lots) => {
  const out = new Map();
  lots.forEach((b) => {
    const k = b.cut_ticket_id || b.id;
    if (!out.has(k)) out.set(k, { key: k, lots: [] });
    out.get(k).lots.push(b);
  });
  return [...out.values()].map((g) => {
    const phase = g.lots.reduce((p, b) => (PHASE_IDS.indexOf(b.phase) < PHASE_IDS.indexOf(p) ? b.phase : p), g.lots[0].phase);
    return { ...g, phase, mixed: g.lots.some((b) => b.phase !== phase) };
  });
};

// Codice articolo dallo SKU (es. 8001-A26-NERO -> 8001 A26)
const articleCode = (b) => {
  const parts = (b.sku || "").split("-");
  const i = parts.findIndex((p, j) => j > 0 && /^[AP]\d{2}$/i.test(p));
  return parts.slice(0, i > 0 ? i + 1 : 2).join(" ").toUpperCase();
};

const PHASES = [
  { id: "programmato", label: "Programmato", icon: CalendarClock, color: "border-foreground/40 bg-foreground/10 text-foreground", card: "border-l-4 border-l-foreground/80" },
  { id: "taglio", label: "Taglio", icon: Scissors, color: "border-yellow-400/40 bg-yellow-400/15 text-yellow-300", card: "border-l-4 border-l-yellow-400 bg-yellow-400/[0.04]" },
  { id: "confezione", label: "Confezione", icon: Shirt, color: "border-orange-500/40 bg-orange-500/15 text-orange-400", card: "border-l-4 border-l-orange-500 bg-orange-500/[0.05]" },
  { id: "controllo", label: "Controllo Qualità", icon: CheckCircle2, color: "border-blue-500/40 bg-blue-500/15 text-blue-400", card: "border-l-4 border-l-blue-500 bg-blue-500/[0.05]" },
  { id: "completato", label: "Completato", icon: Package2, color: "border-emerald-500/40 bg-emerald-500/15 text-emerald-400", card: "border-l-4 border-l-emerald-500 bg-emerald-500/[0.05]" },
];
const phaseMeta = (id) => PHASES.find((p) => p.id === id) || PHASES[0];
const PHASE_IDS = PHASES.map((p) => p.id);

export default function Produzione() {
  const qc = useQueryClient();

  const [range, setRange] = useState(null);
  const prev = previousRange(range);
  const { data: seasons } = useQuery({ queryKey: ["production-seasons"], queryFn: async () => (await api.get("/production/seasons")).data });
  const [seasonPick, setSeason] = useState(null);
  const season = seasonPick || seasons?.current;
  const sp = (r) => ({ ...dateParams(r), season });
  const { data: batches = [], isLoading: lb } = useQuery({ queryKey: ["production", range, season], enabled: !!season, queryFn: async () => (await api.get("/production", { params: sp(range) })).data });
  const { data: prevBatches = [] } = useQuery({ queryKey: ["production", "prev", range, season], queryFn: async () => (await api.get("/production", { params: sp(prev) })).data, enabled: !!prev && !!season });

  const [search, setSearch] = useState("");
  const [phaseFilter, setPhaseFilter] = useState(null);
  const groups = useMemo(() => groupLots(batches), [batches]);
  const prevGroups = useMemo(() => groupLots(prevBatches), [prevBatches]);
  const shownGroups = useMemo(() => {
    const words = searchWords(search);
    return groups.filter((g) => (!phaseFilter || g.phase === phaseFilter) && g.lots.some((b) => productMatches({ name: `${b.product_name} ${b.sheet_name || ""} ${b.batch_number} ${b.lab || ""} ${b.category || ""}`, color: b.color, season: b.season, sku: b.sku }, words)));
  }, [groups, search, phaseFilter]);

  const [move, setMove] = useState(null);
  const prevPhase = (cur) => {
    const idx = PHASES.findIndex((p) => p.id === cur);
    return idx > 0 ? PHASES[idx - 1].id : null;
  };

  const nextPhase = (cur) => {
    const idx = PHASES.findIndex((p) => p.id === cur);
    return idx < PHASES.length - 1 ? PHASES[idx + 1].id : null;
  };

  return (
    <div>
      <PageHeader title="Produzione" subtitle="Lotti di produzione, laboratori, fallati e taglio interno.">
        {seasons && <SeasonSelect value={season} options={seasons.options} current={seasons.current} onChange={setSeason} testid="produzione-season-select" />}
        <DateRangeFilter range={range} onChange={setRange} testid="produzione-date-filter" />
      </PageHeader>

      <Tabs defaultValue="lotti">
        <TabsList className="mb-6 max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="lotti" data-testid="tab-lotti">Lotti di produzione</TabsTrigger>
          <TabsTrigger value="laboratori" data-testid="tab-laboratori">Laboratori</TabsTrigger>
          <TabsTrigger value="fallati" data-testid="tab-fallati">Fallati</TabsTrigger>
          <TabsTrigger value="taglio" data-testid="tab-taglio">Taglio</TabsTrigger>
        </TabsList>

        {/* LOTTI */}
        <TabsContent value="lotti">
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-5">
            {PHASES.map((ph) => {
              const count = groups.filter((g) => g.phase === ph.id).length;
              const prevCount = prevGroups.filter((g) => g.phase === ph.id).length;
              const active = phaseFilter === ph.id;
              return (
                <Card key={ph.id} role="button" tabIndex={0} aria-pressed={active}
                  onClick={() => setPhaseFilter(active ? null : ph.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setPhaseFilter(active ? null : ph.id); } }}
                  className={`cursor-pointer p-4 transition-colors hover:border-primary/60 ${active ? "border-primary bg-primary/10 ring-1 ring-primary" : phaseFilter ? "opacity-60" : ""}`}
                  data-testid={`phase-filter-${ph.id}`}>
                  <ph.icon className="h-5 w-5 text-muted-foreground" />
                  <div className="mt-2 flex items-baseline gap-2">
                    <p className="font-display text-2xl font-bold">{count}</p>
                    <Delta current={count} previous={prev ? prevCount : null} />
                  </div>
                  <p className="text-xs text-muted-foreground">{ph.label}</p>
                </Card>
              );
            })}
          </div>

          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="relative w-full sm:w-[560px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" placeholder="Cerca lotto, codice (es. 8001 a26 nero), articolo, colore, laboratorio..." data-testid="batch-search" />
            </div>
            <div className="flex items-center gap-2">
              {phaseFilter && (
                <Button variant="ghost" size="sm" onClick={() => setPhaseFilter(null)} data-testid="phase-filter-reset">
                  Mostra tutte le fasi ({phaseMeta(phaseFilter).label})
                </Button>
              )}
              <NewBatchDialog phases={PHASES} />
            </div>
          </div>

          {lb || !season ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : shownGroups.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground" data-testid="batches-empty">{batches.length ? (phaseFilter && !search ? `Nessun lotto in fase ${phaseMeta(phaseFilter).label}.` : "Nessun lotto corrisponde alla ricerca.") : season === "tutti" ? "Nessun lotto nel periodo scelto." : `Nessun lotto per la stagione ${season} nel periodo scelto.`}</p>
          ) : (
            <div className="space-y-6">
              {shownGroups.map((g) => {
                const h = g.lots[0];
                const code = articleCode(h);
                const gm = phaseMeta(g.phase);
                const next = nextPhase(g.phase);
                const back = prevPhase(g.lots.reduce((p, b) => (PHASE_IDS.indexOf(b.phase) > PHASE_IDS.indexOf(p) ? b.phase : p), g.phase));
                return (
                  <Card key={g.key} className={`overflow-hidden border-2 border-border shadow-md ${gm.card}`} data-testid={`batch-group-${h.cut_ticket_id || h.batch_number}`}>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b-2 border-border bg-secondary/70 px-5 py-3">
                      {h.image_url && <img src={h.image_url} alt="" className="h-14 w-11 shrink-0 rounded object-cover" />}
                      <span className="rounded-md bg-primary px-3 py-1 font-mono-erp text-base font-bold tracking-wider text-primary-foreground" data-testid={`batch-code-${h.batch_number}`}>{code || "SENZA CODICE"}</span>
                      <div className="order-last w-full min-w-0 sm:order-none sm:w-auto sm:flex-1">
                        <p className="font-display text-lg font-semibold leading-tight">{h.product_name}</p>
                        <p className="text-xs text-muted-foreground">{[h.category, g.lots.length > 1 ? `${g.lots.length} colori` : null, `${num(g.lots.reduce((t, b) => t + (b.quantity || 0), 0))} pz in totale`].filter(Boolean).join(" · ")}</p>
                        {h.sheet_id && !h.product_id && <p className="text-[10px] text-primary" data-testid={`batch-sheet-${h.batch_number}`}>Da scheda modello · si collegherà a Shopify tramite il codice</p>}
                        {h.sheet_id && h.product_id && h.sheet_name && h.sheet_name !== h.product_name && <p className="text-[10px] text-muted-foreground">Scheda modello: {h.sheet_name}</p>}
                      </div>
                      <div className="flex items-center gap-2 font-mono-erp text-xs">
                        {season === "tutti" && h.season && <span className="rounded border border-border px-2 py-0.5">{h.season}</span>}
                        {h.cut_ticket_number && <span className="rounded bg-primary/15 px-2 py-0.5 font-semibold text-primary" data-testid={`batch-ticket-${h.batch_number}`}>{h.cut_ticket_number}° tagliato</span>}
                        <Badge variant="outline" className={`text-sm ${gm.color}`} data-testid={`group-phase-${h.batch_number}`}><gm.icon className="mr-1 h-3.5 w-3.5" />{gm.label}</Badge>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 border-b border-border/60 px-5 py-3">
                      {g.mixed && <p className="flex items-center gap-1.5 text-xs text-amber-400" data-testid={`group-mixed-${h.batch_number}`}><AlertTriangle className="h-3.5 w-3.5" />I colori sono in fasi diverse: avanzando li porti tutti insieme.</p>}
                      <div className="ml-auto flex gap-2">
                        {back && <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setMove({ batches: g.lots, phase: back, back: true })} data-testid={`back-${h.batch_number}`}>Riporta a {phaseMeta(back).label}</Button>}
                        {next && <Button size="sm" onClick={() => setMove({ batches: g.lots, phase: next })} data-testid={`advance-${h.batch_number}`}>{g.lots.length > 1 ? "Avanza tutto a" : "Avanza a"}: {phaseMeta(next).label}</Button>}
                      </div>
                    </div>
                    {(g.phase === "confezione" || h.lab) && <div className="px-5 pt-3"><LabInfo key={`${h.id}-${h.lab}-${h.expected_delivery}`} batch={h} ids={g.lots.map((b) => b.id)} /></div>}
                    <div className="px-5"><PhaseTimeline history={h.phase_history} phaseMeta={phaseMeta} /></div>
                    <div className="grid grid-cols-1 items-start gap-4 p-4 md:grid-cols-2 xl:grid-cols-3">
                    {g.lots.map((b) => {
                const meta = phaseMeta(b.phase);
                return (
                  <div key={b.id} className="rounded-md border border-border/60 bg-background/40 p-4" data-testid={`batch-${b.batch_number}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold uppercase tracking-wide" data-testid={`batch-color-${b.batch_number}`}>{b.color || "Senza colore"} <span className="text-sm font-normal normal-case text-muted-foreground">· {num(b.quantity)} pz</span></p>
                        <p className="font-mono-erp text-[11px] text-muted-foreground">{b.batch_number}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {g.mixed && <Badge variant="outline" className={meta.color}><meta.icon className="mr-1 h-3 w-3" />{meta.label}</Badge>}
                        <EditQuantitiesButton batch={b} />
                        <DeleteBatchButton batch={b} />
                      </div>
                    </div>
                    <BatchSizes sizes={b.sizes} />
                    <QuantityChanges batch={b} phaseMeta={phaseMeta} />
                    {b.notes && <p className="mt-3 text-xs text-muted-foreground border-l-2 border-border pl-2">{b.notes}</p>}
                    <StockMoves batch={b} />
                    {b.fabric_plan && (b.fabric_plan.length > 0 || b.fabric_plan_notes?.length > 0) && (
                      <div className="mt-3 rounded-md border border-yellow-400/30 bg-yellow-400/[0.05] p-3" data-testid={`fabric-plan-${b.batch_number}`}>
                        <p className="mb-2 font-mono-erp text-[11px] uppercase tracking-widest text-yellow-300">Tessuto da scalare in Taglio · {b.color || "questo colore"}</p>
                        <div className="space-y-1.5">
                          {b.fabric_plan.map((i) => (
                            <div key={`${i.material_id}-${i.fused}`} className="flex items-center justify-between gap-3 text-xs">
                              <span>{i.name} <span className="text-muted-foreground">{[i.color, i.supplier].filter(Boolean).join(" · ")}</span></span>
                              <span className={`font-medium ${i.sufficient ? "" : "text-rose-400"}`} data-testid={`fabric-plan-${b.batch_number}-${i.material_id}`}>{num(i.needed)} {i.unit}</span>
                            </div>
                          ))}
                        </div>
                        {b.fabric_plan_notes?.map((n) => <p key={n} className="mt-1.5 text-[11px] text-amber-400">{n}</p>)}
                        {b.fabric_plan.length > 0 && <p className="mt-2 text-[11px] text-muted-foreground">Dagli strati della scheda taglio per questo colore, già con il 7% per difetti e margine di stesura.</p>}
                      </div>
                    )}
                    {b.fabric_check?.length > 0 && (
                      <div className="mt-3 flex items-start gap-1.5 rounded bg-rose-500/10 px-2 py-1.5 text-xs text-rose-400" data-testid={`fabric-short-${b.batch_number}`}>
                        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>Tessuto non sufficiente per gli strati della scheda taglio: {b.fabric_check.map((i) => `${i.name} ${i.color || ""} servono ${num(i.needed)} m, ${i.shared ? "disponibili dopo gli altri lotti programmati" : "in magazzino"} ${num(i.available)} m`).join(" · ")}</span>
                      </div>
                    )}
                    {b.consumption && b.consumption.length > 0 && (
                        <div className="mt-3 rounded-md border border-border/60 p-3">
                          <p className="font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Materiale stimato per {b.color || "questo colore"} ({num(b.quantity)} capi)</p>
                          <div className="space-y-1.5">
                            {b.consumption.map((c) => (
                              <div key={c.material_id} className="flex items-center justify-between text-xs">
                                <span>{c.material_name}</span>
                                <span className={c.sufficient ? "font-medium" : "font-medium text-rose-400"}>
                                  {num(c.needed)} {c.unit}
                                  {!c.sufficient && ` · mancano ${num(Math.max(0, c.needed - c.available))} ${c.unit}`}
                                  {!c.sufficient && c.arriving > 0 && ` (${num(c.arriving)} in arrivo)`}
                                </span>
                              </div>
                            ))}
                          </div>
                          <p className="mt-2 text-[11px] text-muted-foreground" data-testid={`sheet-check-${b.batch_number}`}>Accessori dalla distinta base, tessuto dagli strati della scheda taglio: il tessuto si scala in Taglio, gli accessori in Confezione.</p>
                        </div>
                    )}
                  </div>
                );
              })}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="laboratori"><LabsSummary /></TabsContent>
        {move && <PhaseMoveDialog batches={move.batches} phase={move.phase} label={phaseMeta(move.phase).label} back={move.back} onClose={() => setMove(null)} />}
        <TabsContent value="fallati"><DefectsReport range={range} /></TabsContent>
        <TabsContent value="taglio"><CuttingTab range={range} /></TabsContent>
      </Tabs>
    </div>
  );
}
